/**
 * UserManagementActions
 *
 * Action bar that keeps the filter trigger aligned to the left
 * and the invite action aligned to the right, following the
 * user-management design specification.
 *
 * ## Features
 * - Shows the number of active filters via Ant Design Badge
 * - Exposes a filter trigger with screen-reader label
 * - Optional primary action for inviting users
 *
 * @param props - Component properties
 * @param props.filterBadgeCount - Number of active filters
 * @param props.filtersLabel - Visible label for the filter button
 * @param props.filtersAriaLabel - Accessible label describing the filter action
 * @param props.inviteLabel - Visible label for the invite button
 * @param props.canInvite - Controls whether the invite button is rendered
 * @param props.onOpenFilters - Callback when the filter button is clicked
 * @param props.onInviteUser - Callback when the invite button is clicked
 * @param props.onRefreshTable - Callback when the refresh button is clicked
 * @param props.refreshing - Whether a refresh is in flight (spins the icon)
 * @param props.refreshAriaLabel - Accessible label for the refresh action
 * @param props.ariaLabel - ARIA label for the button group
 *
 * @returns Action buttons component for the User Management page
 *
 * @example
 * ```tsx
 * <UserManagementActions
 *   filterBadgeCount={2}
 *   filtersLabel="Filtros"
 *   filtersAriaLabel="Abrir filtros"
 *   inviteLabel="+ Convidar usuário"
 *   canInvite
 *   onOpenFilters={() => {}}
 *   onInviteUser={() => {}}
 * />
 * ```
 *
 * @since 1.0.0
 * @category Molecules
 */
import React from 'react';
import { Badge } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { Button } from '@/shared/components/ui/atoms';
import FilterAltIcon from '@/assets/icons/filter_alt.svg?react';
import FilterRefresh from '@/assets/icons/refresh.svg?react';

import './UserManagementActions.css';

export interface UserManagementActionsProps {
  filterBadgeCount: number;
  filtersLabel: string;
  filtersAriaLabel: string;
  inviteLabel: string;
  canInvite?: boolean;
  onOpenFilters: () => void;
  onInviteUser?: () => void;
  onRefreshTable?: () => void;
  refreshing?: boolean;
  refreshAriaLabel?: string;
  ariaLabel?: string;
}

const noop = () => undefined;

export const UserManagementActions: React.FC<UserManagementActionsProps> = ({
  filterBadgeCount,
  filtersLabel,
  filtersAriaLabel,
  inviteLabel,
  canInvite = false,
  onOpenFilters,
  onInviteUser = noop,
  onRefreshTable,
  refreshing = false,
  refreshAriaLabel = 'Refresh table',
  ariaLabel = 'User management actions',
}) => {
  return (
    <div
      className="user-management-actions"
      role="group"
      aria-label={ariaLabel}
    >
      <div className="user-management-actions__filters">
        <Badge
          count={filterBadgeCount}
          size="small"
          overflowCount={99}
          className="user-management-actions__badge"
        >
          <Button
            variant="ghost"
            icon={<FilterAltIcon />}
            iconPosition="end"
            onClick={onOpenFilters}
            aria-label={filtersAriaLabel}
            fullWidth={false}
            className="user-management-actions__open-filter"
          >
            {filtersLabel}
          </Button>
        </Badge>

        <Button
          variant="ghost"
          onClick={onRefreshTable}
          fullWidth={false}
          disabled={refreshing}
          aria-label={refreshAriaLabel}
          className={[
            'user-management-actions__refresh',
            refreshing ? 'user-management-actions__refresh--loading' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <FilterRefresh />
        </Button>
      </div>

      {canInvite && (
        <div className="user-management-actions__primary">
          <Button
            variant="primary"
            fullWidth={false}
            icon={<PlusOutlined />}
            onClick={onInviteUser}
          >
            {inviteLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export default UserManagementActions;
